import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function EastSussexCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="East Sussex Pothole Claims | £23m Funding Gap | Fixtyer"
        description="East Sussex County Council admits it needs £23m a year just to stop roads declining, filled 106,063 potholes in five years, and submitted a nil return for 2024 unclassified road surveys. See what the DfT all-AMBER rating means for your claim."
        canonical="/councils/east-sussex"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'East Sussex', url: '/councils/east-sussex' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'East Sussex is AMBER on spend and best practice as well as condition — can I still claim?',
            answer: 'Yes. All four DfT scorecards are AMBER, but Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate ratings. East Sussex\'s own data shows capital spend forecast at £16.7m against a £21m DfT allocation in 2025/26, reactive maintenance rising to 7.4%, and road condition gradually declining. A council that publishes those figures cannot argue its network is well resourced in the abstract.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 1,994km — 59% of East Sussex\'s 3,375km network — and 18% were in RED condition at the last published survey in 2025. That is roughly 359km of estate roads, village lanes and residential streets. The council itself states that "the first roads to show signs of decline are usually the local unclassified roads" and that with limited funds it must prioritise A and B roads.',
          },
          {
            question: 'Does the 2024 nil return for unclassified road surveys help my claim?',
            answer: 'It can. East Sussex carried out a Course Visual Inspection of U-roads in 2024 but concluded the results "were not considered to be accurate or reflective of the actual road condition" because the contractor lacked qualified inspectors and the survey slipped into winter. The DfT advised a nil return for 2024. For incidents in that period on U-roads, the council had no valid network-level condition data for the road class that makes up nearly three-fifths of its network.',
          },
          {
            question: 'Does the £23m annual funding gap admission help my claim?',
            answer: 'Yes. East Sussex\'s own modelling found it would cost £23m per year to keep carriageways in the same condition overall, and the council admits funding "has been significantly less than the amount needed to achieve this" for seven years. Lack of money is not a Section 58 defence — the statutory test is whether the council took reasonable care — but it is documented knowledge that the network is under-funded relative to the council\'s own steady-state estimate.',
          },
          {
            question: 'Pothole repairs fell in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. East Sussex still filled 19,381 pothole safety defects in 2024/25 — about 53 every day — down from a peak of 27,134 in 2023/24 after one of the wettest winters on record. The council attributes the drop partly to an extensive patching programme, not to a network-wide recovery. Its funding tables forecast 23,746 pothole repairs in 2025/26.',
          },
          {
            question: 'How often does East Sussex actually survey my road?',
            answer: 'A, B and C roads receive annual SCANNER surveys using sensor-equipped vehicles. Unclassified roads rely on Course Visual Inspections — drive-by visual assessments rather than laser measurement. When the 2024 U-road CVI failed validation, the council had no publishable condition data at all for that year. For most residential roads, your strongest structural argument may be the combination of CVI methodology, the 2024 nil return, and 18% RED at the last valid survey.',
          },
        ]}
      />
      <ServiceSchema
        councilName="East Sussex County Council"
        councilSlug="east-sussex"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/east-sussex"
        headline="East Sussex Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">East Sussex</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            East Sussex: All-Amber Rating, £23m Steady-State Gap
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            East Sussex earns <strong className="text-amber-400">AMBER on all four DfT scorecards</strong> — condition,
            spend, and best practice included. The council's own modelling says it needs{' '}
            <strong className="text-rose-400">£23m a year</strong> just to stop carriageways declining, yet admits seven
            years of funding shortfall. It filled <strong className="text-rose-400">106,063 potholes in five years</strong>,
            invalidated its 2024 unclassified-road survey, and still reports 18% of U-roads in RED condition.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">18%</div>
            <div className="text-white font-medium mt-2">Unclassified roads in RED condition (2025)</div>
            <div className="text-brand-300 text-sm mt-1">
              Roughly 359km of the 1,994km U-road network — nearly three-fifths of East Sussex's roads — classed as
              needing major maintenance intervention at the last valid survey.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Six years of SCANNER and CVI data from East Sussex's own transparency reporting — classified roads
            treading water, local roads carrying the decline
          </p>

          {/* Network size */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">372km</div>
              <div className="text-sm text-surface-600">A-roads (11%)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">1,009km</div>
              <div className="text-sm text-surface-600">B and C roads (30%)</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-amber-700">1,994km</div>
              <div className="text-sm text-amber-800">U-roads (59%)</div>
            </div>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (372km): stable but not improving</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4.0%</td>
                    <td className="px-4 py-2 text-center">32.9%</td>
                    <td className="px-4 py-2 text-center">63.1%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">4.4%</td>
                    <td className="px-4 py-2 text-center">28.3%</td>
                    <td className="px-4 py-2 text-center">67.4%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2025</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.3%</td>
                    <td className="px-4 py-2 text-center font-bold">30.4%</td>
                    <td className="px-4 py-2 text-center font-bold">64.2%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED share has edged up from 4.0% to 5.3% over six years. Green-condition A-roads peaked at 67.4%
              in 2023 and have slipped back. Main routes are holding up — but a third still need attention or will soon.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (1,009km): RED share rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4.4%</td>
                    <td className="px-4 py-2 text-center">27.8%</td>
                    <td className="px-4 py-2 text-center">67.7%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">6.1%</td>
                    <td className="px-4 py-2 text-center">30.4%</td>
                    <td className="px-4 py-2 text-center">63.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">6.1%</td>
                    <td className="px-4 py-2 text-center">31.0%</td>
                    <td className="px-4 py-2 text-center">62.9%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5.3%</td>
                    <td className="px-4 py-2 text-center">25.6%</td>
                    <td className="px-4 py-2 text-center">69.4%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center">6.1%</td>
                    <td className="px-4 py-2 text-center">27.7%</td>
                    <td className="px-4 py-2 text-center">66.2%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2025</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6.5%</td>
                    <td className="px-4 py-2 text-center font-bold">29.4%</td>
                    <td className="px-4 py-2 text-center font-bold">64.6%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B/C roads are up nearly half since 2020 (4.4% → 6.5%), and combined RED plus amber now
              stands at 35.9% — over a third of the 1,009km B/C network flagged for maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The £23m Steady-State the Council Cannot Fund</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-rose-400">£23m</div>
                <div className="text-brand-300 text-sm">Annual cost to hold carriageway condition steady</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">£21m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">£16.7m</div>
                <div className="text-brand-300 text-sm">Forecast total capital spend 2025/26</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              East Sussex's own lifecycle modelling says{' '}
              <span className="text-rose-400">£23m a year</span> is needed just to stand still on carriageways — and
              the council admits seven years of spending below that level. For 2025/26, forecast capital spend is{' '}
              <span className="text-amber-400">below the DfT allocation</span>, not above it. That is why the Spend
              scorecard is AMBER too.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Blind Spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 1,994km Unclassified Problem
          </h2>
          <p className="text-surface-600 mb-8">
            59% of the network surveyed by visual inspection — and the 2024 data did not survive validation
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">19%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Nil return — survey invalidated</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2025</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Although a CVI survey of U roads took place in 2024, the results were not considered to be accurate or reflective of the actual road condition. This was due to the contractor lacking sufficient qualified inspectors to deliver the survey during the summer months as agreed. The delay pushed survey activity into the winter months (February 2025) when conditions are widely recognised as unsuitable for visual inspections."
              source="East Sussex County Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Therefore the DFT advised us to submit a nil return for 2024."
              source="East Sussex County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">CVI, Not SCANNER</h3>
              <p className="text-surface-600 text-sm mb-3">
                East Sussex SCANNER-surveys all A, B and C roads annually with sensor-equipped vehicles. For
                unclassified roads it uses Course Visual Inspections — a drive-by visual assessment. That is a
                materially different standard of knowledge than laser-measured classified roads.
              </p>
              <p className="text-surface-600 text-sm">
                At 18% RED in 2025, roughly <strong>359km of residential and rural lanes</strong> are in the category
                the council defines as likely requiring major maintenance intervention.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 2024 Data Void</h3>
              <p className="text-amber-800 text-sm mb-3">
                A resampling exercise in June 2025 found results "broadly in line with historic condition trends" and
                showed the original winter survey "was not representative of the network" — but the sample was too
                small to calculate a replacement average.
              </p>
              <p className="text-amber-800 text-sm">
                For pothole incidents on U-roads in 2024, there is{' '}
                <strong>no valid published network condition data</strong> at all.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing the
              condition of its roads. For East Sussex's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road last condition-surveyed by CVI — and was it before or after the invalidated 2024 survey?</li>
              <li>• If 18% of U-roads were RED at the last valid survey, what was done about yours specifically?</li>
              <li>• How does a visual drive-by catch a pothole forming between inspections?</li>
              <li>• Can the council claim detailed network knowledge when it submitted a nil return for an entire survey year?</li>
            </ul>
            <p className="text-white font-medium">
              A council that prioritises A and B roads, surveys U-roads by eye, and throws away a whole year of data
              cannot claim equal knowledge across its network.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            106,063 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Safety-defect pothole repairs from East Sussex's own published tables — peaking after the wet winter of 2023/24
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (safety defects)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">19,261</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">18,250</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">22,037</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">27,134</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">19,381</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">106,063</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~58 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, East Sussex fills around 58 pothole safety defects per day. The council
                notes carriageway potholes typically make up approximately 70% of all safety defects repaired at a
                fixed reactive cost of £3.17m per year. A network producing hazards at that rate is one where defects
                routinely form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Wet-Winter Spike</h3>
              <p className="text-amber-800 text-sm">
                The council attributes the jump to 27,134 potholes in 2023/24 to "one of the wettest winters on
                record." The drop to 19,381 in 2024/25 partly reflects an extensive patching programme funded by DfT
                grants and council borrowing — not a claim that the underlying network has recovered.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Funding Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Funding and Prioritisation Admissions
          </h2>
          <p className="text-surface-600 mb-8">
            Why all four DfT scorecards are AMBER — in the council's own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The overall condition of roads in East Sussex has been gradually declining."
              source="East Sussex County Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Over the last seven years, East Sussex Highways has faced a fluctuating level of investment in carriageway maintenance. The funding available to spend on carriageways has been significantly less than the amount needed to achieve this. This major funding shortfall, combined with inflation and rising costs, has meant we have not been able to invest as much in preventative maintenance as needed to prevent long term decline."
              source="East Sussex County Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The first roads to show signs of decline are usually the local unclassified roads. These were often not constructed as robustly as more major roads and without maintenance they will generally deteriorate more quickly. However, with limited funds we must prioritise our A and B roads, which are key for business, transport and emergency services. It is not a choice we take lightly, but it is widely regarded as the most effective way to manage risk and keep the county moving with the resources we have."
              source="East Sussex County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Total capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative %</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive %</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£18.0m</td>
                  <td className="px-4 py-3 text-center">£25.8m</td>
                  <td className="px-4 py-3 text-center">£13.4m</td>
                  <td className="px-4 py-3 text-center">96.2%</td>
                  <td className="px-4 py-3 text-center">3.8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£17.3m</td>
                  <td className="px-4 py-3 text-center">£33.9m</td>
                  <td className="px-4 py-3 text-center">£15.6m</td>
                  <td className="px-4 py-3 text-center">94.0%</td>
                  <td className="px-4 py-3 text-center">6.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£14.9m</td>
                  <td className="px-4 py-3 text-center">£28.5m</td>
                  <td className="px-4 py-3 text-center">£13.2m</td>
                  <td className="px-4 py-3 text-center">92.6%</td>
                  <td className="px-4 py-3 text-center">7.4%</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (forecast)</td>
                  <td className="px-4 py-3 text-center font-bold">£21.0m</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-700">£16.7m</td>
                  <td className="px-4 py-3 text-center">£15.3m</td>
                  <td className="px-4 py-3 text-center text-surface-400 italic">TBC</td>
                  <td className="px-4 py-3 text-center text-surface-400 italic">TBC</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Reactive Share Doubling</h3>
              <p className="text-surface-600 text-sm">
                Preventative maintenance's share has fallen from 96.2% to 92.6% while reactive rose from 3.8% to
                7.4% — nearly double. The council's 2025/26 capital programme targets 76.4km of planned preventative
                work while revenue funds continue to cover reactive pothole and safety-defect repairs.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Below Allocation in 2025/26</h3>
              <p className="text-amber-800 text-sm">
                Unlike councils that overspend their DfT allocation, East Sussex forecasts £16.7m total capital against
                a £21m DfT grant — partly because £4.333m was spent early at the end of 2024/25 on preparatory patching.
                The Spend scorecard is AMBER because the council is not keeping pace with its allocation, not because
                it lacks an asset management strategy on paper.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What all-AMBER means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an All-Amber Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: East Sussex is not Buckinghamshire — but it is not a free win either
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Published asset management strategy aligned with DfT incentive funding criteria
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual SCANNER surveys on all A, B and C roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 24/7 reactive and emergency defect response published in its funding disclosure
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Still ~93% preventative spend in 2024/25 — the council is not purely firefighting
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Extensive 2023/24 patching programme credited with reducing 2024/25 pothole volumes
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on A and B roads with recent SCANNER data. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> All four DfT scorecards AMBER — condition, spend, and best practice
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> £23m steady-state gap admitted — seven years of under-investment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 18% of U-roads RED — council prioritises A/B over local roads by policy
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2024 U-road nil return — no valid condition data for 59% of the network that year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 106,063 pothole safety defects in five years — defects form faster than surveys catch them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C RED share up from 4.4% to 6.5% since 2020
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with published asset management and annual SCANNER data on classified roads, your claim
              lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, East Sussex Highways reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the CVI methodology and 2024 nil return are your strongest structural arguments</li>
              <li>• Timing relative to the wet winter of 2023/24 and the 27,134-repair spike the council itself documents</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              East Sussex's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in East Sussex?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            An all-amber council still owes a duty of care on your specific road. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road nil-return argument</li>
                <li>• No prior-report search</li>
                <li>• No £23m funding-gap citation</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 2024 U-road survey failure documented</li>
                <li>✅ £23m steady-state gap cited</li>
                <li>✅ 106,063 repairs in five years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to East Sussex</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=east-sussex"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My East Sussex Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                East Sussex is AMBER on spend and best practice as well as condition — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. All four DfT scorecards are AMBER, but Section 58 turns on whether the specific defect that
                damaged your vehicle was reasonably inspected and repaired — not on aggregate ratings. East Sussex's
                own data shows capital spend forecast at £16.7m against a £21m DfT allocation in 2025/26, reactive
                maintenance rising to 7.4%, and road condition gradually declining. A council that publishes those
                figures cannot argue its network is well resourced in the abstract.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 1,994km — 59% of East Sussex's 3,375km network — and 18% were in RED
                condition at the last published survey in 2025. That is roughly 359km of estate roads, village lanes
                and residential streets. The council itself states that "the first roads to show signs of decline are
                usually the local unclassified roads" and that with limited funds it must prioritise A and B roads.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the 2024 nil return for unclassified road surveys help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. East Sussex carried out a Course Visual Inspection of U-roads in 2024 but concluded the results
                "were not considered to be accurate or reflective of the actual road condition" because the contractor
                lacked qualified inspectors and the survey slipped into winter. The DfT advised a nil return for 2024.
                For incidents in that period on U-roads, the council had no valid network-level condition data for the
                road class that makes up nearly three-fifths of its network.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the £23m annual funding gap admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. East Sussex's own modelling found it would cost £23m per year to keep carriageways in the same
                condition overall, and the council admits funding "has been significantly less than the amount needed
                to achieve this" for seven years. Lack of money is not a Section 58 defence — the statutory test is
                whether the council took reasonable care — but it is documented knowledge that the network is
                under-funded relative to the council's own steady-state estimate.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. East Sussex still filled 19,381 pothole safety defects in 2024/25 — about 53 every day — down from
                a peak of 27,134 in 2023/24 after one of the wettest winters on record. The council attributes the
                drop partly to an extensive patching programme, not to a network-wide recovery. Its funding tables
                forecast 23,746 pothole repairs in 2025/26.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How often does East Sussex actually survey my road?
              </h3>
              <p className="text-surface-600 text-sm">
                A, B and C roads receive annual SCANNER surveys using sensor-equipped vehicles. Unclassified roads
                rely on Course Visual Inspections — drive-by visual assessments rather than laser measurement. When
                the 2024 U-road CVI failed validation, the council had no publishable condition data at all for that
                year. For most residential roads, your strongest structural argument may be the combination of CVI
                methodology, the 2024 nil return, and 18% RED at the last valid survey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.eastsussex.gov.uk/roads-transport/roads/maintenance"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              East Sussex County Council Local Highways Maintenance Transparency Report 2025
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
